import { Theme } from '@mui/material/styles'

/**
 * The page's side gutter in theme spacing units: 16px at every width. Every row of a page — its
 * header, a list's tabs, toolbar, entries and pagination — insets by this much on both sides, so a
 * page has one left and one right edge from the title down to the last row. The editor top bar pads
 * by the same value, so going from a library into an editor keeps the title and the close button on
 * one left edge and the primary actions on one right edge.
 */
export const PAGE_GUTTER = 2

/**
 * The same gutter as a length, for properties MUI does not read as spacing units — `left` and
 * `right` on an absolutely positioned element take a number as pixels.
 */
export const pageGutterOffset = (theme: Theme) => theme.spacing(PAGE_GUTTER)

/**
 * The height of the header row on every page and in every editor. Low, so a list shows more of its
 * entries and an editor more of its canvas; one value, so moving from a page into its editor does
 * not shift the divider under the title.
 */
export const PAGE_HEADER_HEIGHT = 52

/** The height of every tab row: a page's, an editor's, a dialog's. The theme's Tabs override applies it. */
export const PAGE_TABS_HEIGHT = 40

/**
 * The row at the top of every app page that carries its title and primary action, directly under
 * the top bar: lists, libraries, the dashboard and the AppPage views. One definition, so a title and
 * its call to action sit at the same height and the same left edge on every page.
 *
 * PAGE_HEADER_HEIGHT on desktop with the content centred.
 * Below `md` the row grows instead: a title and two buttons do not fit a 390px line, and a fixed
 * height clipped the second line.
 *
 * No return type annotation on purpose: these are spread into a host's own `sx`, and `SxProps` is a
 * union that includes an array, which is not spreadable.
 */
export const pageHeaderRowSx = {
  minHeight: PAGE_HEADER_HEIGHT,
  height: { xs: 'auto', md: PAGE_HEADER_HEIGHT },
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  rowGap: 1,
  alignItems: 'center',
  pl: PAGE_GUTTER,
  pr: PAGE_GUTTER,
  py: { xs: 1, md: 0 },
  flexShrink: 0,
}

/** The same row on a page whose content does not draw its own divider under the header. */
export const pageHeaderBarSx = { ...pageHeaderRowSx, borderBottom: 1, borderColor: 'divider' }
